import { Package } from 'lucide-react';
import { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { ConfigurationCriteriaProductsRecapHeader } from 'src/features/configuration/configurationCriteria/configurationCriteriaProducts/ConfigurationCriteriaProductsRecapHeader';
import { ConfigurationCriteriaProductsRecapList } from 'src/features/configuration/configurationCriteria/configurationCriteriaProducts/ConfigurationCriteriaProductsRecapList';

type ConfigurationCriteriaProductsRecapProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  validSkus: string[];
  invalidSkus: string[];
  onSubmit: () => void;
  onCancel: () => void;
};

export function ConfigurationCriteriaProductsRecap({
  open,
  onOpenChange,
  validSkus,
  invalidSkus,
  onSubmit,
  onCancel,
}: ConfigurationCriteriaProductsRecapProps): ReactElement {
  const { t } = useTranslation('configuration');
  const totalSkus = useMemo(
    () => validSkus.length + invalidSkus.length,
    [validSkus, invalidSkus],
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className='max-h-[80vh] max-w-2xl' closable={false}>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Package className='h-5 w-5' />
            {t('Import recap')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Check the products that will be imported and those that will be ignored',
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-6'>
          <ConfigurationCriteriaProductsRecapHeader
            invalidSkus={invalidSkus.length}
            totalSkus={totalSkus}
            validSkus={validSkus.length}
          />
          <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <ConfigurationCriteriaProductsRecapList
              description={t('The following products will be imported')}
              listClassName='border-success-border bg-success-background'
              skus={validSkus}
              title={t('Valid products')}
              titleClassName='text-success-default'
            />
            <ConfigurationCriteriaProductsRecapList
              description={t('The following products will be ignored')}
              listClassName='border-error-border bg-error-background'
              skus={invalidSkus}
              title={t('Invalid products')}
              titleClassName='text-error-default'
            />
          </div>
        </div>
        <DialogFooter className='gap-2'>
          <Button onClick={onCancel} variant='outline'>
            {t('Cancel')}
          </Button>
          <Button
            className='min-w-[120px]'
            disabled={validSkus.length === 0}
            onClick={onSubmit}
          >
            {validSkus.length === 0
              ? t('No valid products')
              : `${t('Import')} ${validSkus.length} ${t('product')}${validSkus.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
